import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { Button } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CustomDateRangePicker from './CustomDateRangePicker';
import { useState, useEffect } from 'react';

export interface SimpleDialogProps {
  open: boolean;
  dateRange: any;
  setDateRange: any;
  onClose: () => void;
  onApply?: () => void;
}

export function SimpleDialog(props: SimpleDialogProps) {
  const { onClose, open } = props;
  const [startDateValue, setStartDateValue] = useState<Date | null>(
    props.dateRange.startDate,
  );
  const [endDateValue, setEndDateValue] = useState<Date | null>(
    props.dateRange.endDate,
  );

  // Reset values when dialog opens
  useEffect(() => {
    if (open) {
      setStartDateValue(props.dateRange.startDate);
      setEndDateValue(props.dateRange.endDate);
    }
  }, [open, props.dateRange.startDate, props.dateRange.endDate]);

  const handleApply = () => {
    // Apply the changes and close
    props.setDateRange({
      startDate: startDateValue,
      endDate: endDateValue,
    });
    props.onApply?.();
    onClose();
  };

  const handleCancel = () => {
    // Reset to original values and close
    setStartDateValue(props.dateRange.startDate);
    setEndDateValue(props.dateRange.endDate);
    onClose();
  };

  const modernDatePickerStyle = {
    width: '240px',
    height: '52px',
    background: '#FFFFFF',
    borderRadius: '12px',
    textAlign: 'center' as const,
    border: '1px solid #E5E7EB',
    fontSize: '16px',
    fontWeight: '500',
    color: '#374151',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    ':focus': {
      outline: 'none',
      borderColor: '#4A148C',
      boxShadow: '0 0 0 3px rgba(74, 20, 140, 0.1)',
    },
    ':hover': {
      borderColor: '#D1D5DB',
      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
    },
  };

  return (
    <Dialog
      onClose={handleCancel}
      open={open}
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'visible',
          maxWidth: '640px',
          width: '90%',
        }
      }}
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FB 100%)',
          padding: '32px',
          borderRadius: '24px',
          position: 'relative',
          minHeight: '400px',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4A148C 0%, #6A1B9A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(74, 20, 140, 0.3)',
            }}>
              <CalendarTodayIcon sx={{ color: 'white', fontSize: '24px' }} />
            </div>
            <div>
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#111827',
                  lineHeight: '1.2',
                  margin: 0,
                }}
              >
                Select Date Range
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  color: '#6B7280',
                  lineHeight: '1.4',
                  margin: 0,
                }}
              >
                Choose your analysis period
              </Typography>
            </div>
          </div>
        </div>

        {/* Date Pickers Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            marginBottom: '48px',
            justifyContent: 'center',
          }}
        >
          {/* Start Date */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Start Date
            </Typography>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}>
              <CustomDateRangePicker
                customInputStyle={modernDatePickerStyle}
                value={startDateValue}
                setValue={setStartDateValue}
              />
            </div>
          </div>

          {/* Separator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            paddingTop: '32px',
          }}>
            <div style={{
              width: '24px',
              height: '2px',
              background: 'linear-gradient(90deg, #E5E7EB 0%, #D1D5DB 50%, #E5E7EB 100%)',
              borderRadius: '2px',
            }} />
          </div>

          {/* End Date */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              End Date
            </Typography>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}>
              <CustomDateRangePicker
                value={endDateValue}
                setValue={setEndDateValue}
                customInputStyle={modernDatePickerStyle}
              />
            </div>
          </div>
        </div>

        {/* Date Range Summary */}
        {startDateValue && endDateValue && (
          <div style={{
            background: 'linear-gradient(135deg, #F3E8FF 0%, #E8D5FF 100%)',
            border: '1px solid #D8B4FE',
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '32px',
            textAlign: 'center',
          }}>
            <Typography sx={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#581C87',
              margin: 0,
            }}>
              Selected Range: {Math.ceil((endDateValue.getTime() - startDateValue.getTime()) / (1000 * 60 * 60 * 24))} days
            </Typography>
            <Typography sx={{
              fontSize: '12px',
              color: '#7C3AED',
              margin: '4px 0 0 0',
            }}>
              {startDateValue.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })} → {endDateValue.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              borderColor: '#D1D5DB',
              color: '#6B7280',
              paddingX: '32px',
              paddingY: '12px',
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '120px',
              height: '48px',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
                color: '#374151',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              background: 'linear-gradient(135deg, #4A148C 0%, #6A1B9A 100%)',
              color: 'white',
              paddingX: '32px',
              paddingY: '12px',
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '120px',
              height: '48px',
              boxShadow: '0 4px 12px rgba(74, 20, 140, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #581C87 0%, #7C2D92 100%)',
                boxShadow: '0 6px 16px rgba(74, 20, 140, 0.4)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
